"use client";

import { ChevronsUpDown, LifeBuoy, LogOut } from "lucide-react";
import Link from "next/link";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { authClient } from "@/lib/auth";
import { SETTINGS_SECTIONS } from "@/lib/navigation";

// Avatar + name + email, rendered identically in the trigger and the open
// menu's header.
function UserIdentity({
  name,
  email,
  image,
}: {
  name: string;
  email: string;
  image: string | null | undefined;
}) {
  return (
    <>
      <Avatar className="size-8">
        <AvatarImage src={image ?? undefined} alt="" />
        <AvatarFallback className="text-xs">
          {(name || email).charAt(0).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="grid flex-1 text-left leading-tight">
        <span className="truncate font-medium text-sm">{name || email}</span>
        <span className="truncate font-normal text-muted-foreground text-xs">{email}</span>
      </div>
    </>
  );
}

// The account menu: everything about the parent (the left nav is about the
// child). Menu entries map to /settings/* pages. Layout design: TZ-471.
export function NavUser() {
  const { data: session } = authClient.useSession();
  // AuthGuard redirects signed-out visitors; this only skips the flash.
  if (!session) return null;
  const { name, email, image } = session.user;

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent">
              <UserIdentity name={name} email={email} image={image} />
              <ChevronsUpDown className="ml-auto size-4 text-muted-foreground" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          {/* Above the trigger, ChatGPT/Claude style; Radix flips it below
              on its own if there's ever no room. */}
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56"
            side="top"
            align="start"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0">
              <div className="flex items-center gap-2 px-1 py-1.5">
                <UserIdentity name={name} email={email} image={image} />
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {SETTINGS_SECTIONS.map((item) => (
                <DropdownMenuItem key={item.href} asChild>
                  <Link href={item.href}>
                    <item.icon />
                    {item.title}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled>
              <LifeBuoy />
              Help & feedback
              <span className="ml-auto text-muted-foreground text-xs">soon</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => authClient.signOut()}>
              <LogOut />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
